import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import EmojiPicker, { EmojiStyle } from "emoji-picker-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

import VoiceEmotionEmbed from "./VoiceEmotionEmbed";
import TherapistDashboard from "./TherapistDashboard";

const skillHelpTexts = {
  Stress:
    "Indicates your ability to manage stress effectively. Try relaxation techniques to improve it.",
  Anxiety:
    "Represents how well you manage anxiety. Mindfulness and deep breathing help reduce anxiety.",
  Sunlight:
    "Tracks your exposure to sunlight, which supports mood and vitamin D production.",
};

const InfoIcon = ({ onClick }) => (
  <span
    onClick={onClick}
    style={{
      marginLeft: 6,
      cursor: "pointer",
      backgroundColor: "#0077cc",
      borderRadius: "50%",
      width: 18,
      height: 18,
      color: "white",
      fontWeight: "bold",
      fontSize: 14,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      userSelect: "none",
    }}
    title="Click for info"
    role="button"
    tabIndex={0}
    aria-label="Show skill explanation"
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") onClick();
    }}
  >
    i
  </span>
);

export default function SolutionDashboard({
  user,
  displayName: propDisplayName = "Anonymous",
  avatar: propAvatar = "🙂",
  onSetDisplayName,
  onSetAvatar,
  onLogout,
  points = 0,
  streak = 0,
  badges = [],
  skills = { stress: 0, anxiety: 0, sunlight: 0 },
  problems = [],
  canRetake,
  lockDate,
  dailyLogs = [],
  onSubmit,
  newBadge,
  onClosePopup,
  practicedToday = false,
  aiPersonalizationConsent = false,
  voiceEmotionConsent = false,
}) {
  const navigate = useNavigate();

  useEffect(() => {
    console.log("SolutionDashboard: practicedToday =", practicedToday);
  }, [practicedToday]);

  const [displayName, setDisplayName] = useState(propDisplayName);
  const [avatar, setAvatar] = useState(propAvatar);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [mood, setMood] = useState(3);
  const [didPractice, setDidPractice] = useState(false);
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState("");

  // NEW: state for weekly reflection
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [weeklyError, setWeeklyError] = useState("");
  const [weeklyData, setWeeklyData] = useState(null); // {patterns, protectiveFactors, tinyExperiment,...}
  const [weeklyModalOpen, setWeeklyModalOpen] = useState(false);

  useEffect(() => setDisplayName(propDisplayName), [propDisplayName]);
  useEffect(() => setAvatar(propAvatar), [propAvatar]);

  const onEmojiClick = (emojiObject) => {
    if (!emojiObject || !emojiObject.emoji) return;
    setAvatar(emojiObject.emoji);
    onSetAvatar(emojiObject.emoji);
    setEmojiPickerOpen(false);
  };

  const onDisplayNameBlur = () => {
    const trimmed = displayName.trim();
    if (trimmed && trimmed !== propDisplayName) {
      onSetDisplayName(trimmed);
    } else {
      setDisplayName(propDisplayName);
    }
  };

  const onAvatarBlur = () => {
    const trimmed = avatar.trim();
    if (trimmed && trimmed !== propAvatar) {
      onSetAvatar(trimmed);
    } else {
      setAvatar(propAvatar);
    }
  };

  const handleSkillInfoClick = (skill) => {
    setSelectedSkill((prev) => (prev === skill ? null : skill));
  };

  const submitMoodEntry = () => {
    if (!note.trim()) {
      setFormError("Please enter a note.");
      return;
    }
    setFormError("");
    onSubmit({ mood, didPractice, note });
    setMood(3);
    setDidPractice(false);
    setNote("");
  };

  const onNoteChange = (e) => {
    setNote(e.target.value);
    if (formError) setFormError("");
  };

  if (!user) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        Please log in to view your dashboard.
      </div>
    );
  }

  // Prepare last 10 mood logs for chart
  const moodData = dailyLogs
    .filter((log) => typeof log.mood === "number")
    .slice(-10)
    .map((log) => ({ date: log.date, mood: log.mood }));

  // Simple heuristic: need at least 4 logs for weekly reflection
  const hasEnoughLogsForWeekly = (dailyLogs || []).length >= 4;

  // NEW: handler to call /api/weekly-reflection
  const handleGenerateWeeklyReflection = async () => {
    if (!user) return;
    setWeeklyLoading(true);
    setWeeklyError("");
    try {
      const resp = await fetch("/api/weekly-reflection", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.uid }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setWeeklyError(
          data?.error || "Could not generate weekly reflection. Please try again."
        );
        setWeeklyData(null);
      } else {
        setWeeklyData(data);
        setWeeklyModalOpen(true);
      }
    } catch (err) {
      console.error("Failed to fetch weekly reflection:", err);
      setWeeklyError("Could not generate weekly reflection. Please try again.");
      setWeeklyData(null);
    } finally {
      setWeeklyLoading(false);
    }
  };

  const BadgePopup = () =>
    !newBadge ? null : (
      <div
        style={{
          position: "fixed",
          top: 20,
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: "#FFD700",
          color: "#222",
          padding: 16,
          borderRadius: 12,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 10000,
          display: "flex",
          alignItems: "center",
          gap: 12,
          userSelect: "none",
        }}
        role="alert"
      >
        <strong style={{ fontSize: 16 }}>🎉 New Badge Earned: {newBadge}</strong>
        <button
          onClick={onClosePopup}
          aria-label="Close badge popup"
          style={{
            background: "transparent",
            border: "none",
            fontWeight: "bold",
            fontSize: 20,
            cursor: "pointer",
            color: "#222",
            padding: 0,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
    );

  // NEW: Weekly Reflection Modal
  const WeeklyReflectionModal = () =>
    !weeklyModalOpen || !weeklyData ? null : (
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.4)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 15000,
        }}
        aria-modal="true"
        role="dialog"
      >
        <div
          style={{
            backgroundColor: "white",
            borderRadius: 12,
            maxWidth: 520,
            width: "90%",
            padding: 20,
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
            fontFamily: "'Segoe UI', sans-serif",
          }}
        >
          <h2 style={{ marginTop: 0 }}>Weekly reflection</h2>
          <p style={{ fontSize: 13, color: "#555" }}>
            This is a self-reflection summary based on your recent Bolo logs. It is not therapy or medical advice.
          </p>

          {weeklyData.weekLabel && (
            <p style={{ fontSize: 14, fontWeight: "600" }}>
              Week summary: <span style={{ fontWeight: "400" }}>{weeklyData.weekLabel}</span>
            </p>
          )}

          {Array.isArray(weeklyData.patterns) && weeklyData.patterns.length > 0 && (
            <>
              <h4 style={{ marginBottom: 6 }}>What stood out</h4>
              <ul style={{ paddingLeft: 18, fontSize: 14 }}>
                {weeklyData.patterns.map((p, idx) => (
                  <li key={idx}>{p}</li>
                ))}
              </ul>
            </>
          )}

          {Array.isArray(weeklyData.protectiveFactors) &&
            weeklyData.protectiveFactors.length > 0 && (
              <>
                <h4 style={{ marginBottom: 6, marginTop: 12 }}>What seems to help</h4>
                <ul style={{ paddingLeft: 18, fontSize: 14 }}>
                  {weeklyData.protectiveFactors.map((p, idx) => (
                    <li key={idx}>{p}</li>
                  ))}
                </ul>
              </>
            )}

          {weeklyData.tinyExperiment && (
            <>
              <h4 style={{ marginBottom: 6, marginTop: 12 }}>Tiny experiment for next week</h4>
              <p style={{ fontSize: 14 }}>{weeklyData.tinyExperiment}</p>
            </>
          )}

          <div
            style={{
              marginTop: 18,
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
            }}
          >
            <button
              onClick={() => setWeeklyModalOpen(false)}
              style={{
                padding: "8px 14px",
                borderRadius: 6,
                border: "1px solid #ccc",
                backgroundColor: "#f5f5f5",
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );

  return (
    <main
      style={{
        maxWidth: 900,
        margin: "30px auto",
        padding: "0 15px 80px",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        color: "#222",
        position: "relative",
      }}
    >
      <BadgePopup />
      <WeeklyReflectionModal />

      {/* Header: Avatar & Name */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 30,
          userSelect: "none",
        }}
      >
        <div className="avatar-picker" style={{ position: "relative" }} tabIndex={-1}>
          <div
            onClick={() => setEmojiPickerOpen(!emojiPickerOpen)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setEmojiPickerOpen(!emojiPickerOpen);
            }}
            style={{
              fontSize: 48,
              width: 70,
              height: 70,
              borderRadius: 10,
              border: "1px solid #ccc",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              userSelect: "none",
              background: emojiPickerOpen ? "#eee" : "transparent",
              transition: "background 0.2s ease",
              textAlign: "center",
            }}
            title="Click to choose avatar"
            aria-label="Choose avatar"
          >
            {avatar}
          </div>
          {emojiPickerOpen && (
            <div
              style={{
                position: "absolute",
                top: "110%",
                left: 0,
                zIndex: 100000,
              }}
            >
              <EmojiPicker onEmojiClick={onEmojiClick} emojiStyle={EmojiStyle.NATIVE} />
            </div>
          )}
        </div>

        <input
          aria-label="Edit display name"
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          onBlur={onDisplayNameBlur}
          style={{
            fontSize: 28,
            fontWeight: "600",
            maxWidth: 320,
            flexGrow: 1,
            padding: "6px 10px",
            borderRadius: 8,
            border: "1px solid #ccc",
            minWidth: 150,
          }}
        />
      </header>

      {/* Progress */}
      <section
        style={{
          backgroundColor: "#f0f6fa",
          borderRadius: 12,
          padding: 20,
          marginBottom: 20,
          display: "flex",
          justifyContent: "center",
          gap: 40,
          flexWrap: "wrap",
        }}
      >
        <ProgressItem label="Points" value={points} />
        <ProgressItem label="Current Streak" value={streak} />
        <ProgressItem label="Badges" value={badges.length} />
      </section>

      {/* Badges */}
      {badges.length > 0 && (
        <section style={{ marginBottom: 20 }}>
          <h3>Badges</h3>
          <ul style={listStyle}>
            {badges.map((badge, idx) => (
              <li key={idx} style={listItemStyle}>
                {badge}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Skills */}
      <section style={{ marginBottom: 20 }}>
        <h3>Skills</h3>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 15,
            flexWrap: "wrap",
          }}
        >
          {["Stress", "Anxiety", "Sunlight"].map((skill) => (
            <div
              key={skill}
              style={{
                textAlign: "center",
                userSelect: "none",
                cursor: "default",
                minWidth: 130,
              }}
            >
              <SkillItem label={skill} value={skills[skill.toLowerCase()]} />
              <InfoIcon onClick={() => handleSkillInfoClick(skill)} />
              {selectedSkill === skill && (
                <p
                  style={{
                    maxWidth: 250,
                    margin: "6px auto 0",
                    fontSize: 14,
                    color: "#317d52",
                    userSelect: "text",
                  }}
                  aria-live="polite"
                >
                  {skillHelpTexts[skill]}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Therapist Dashboard */}
      <section style={{ marginBottom: 20 }}>
        <h3>🔒 Pro Therapist Dashboard</h3>
        <TherapistDashboard disabled={!canRetake} />
      </section>

      {/* Personalized Solutions */}
      <section style={{ marginBottom: 20 }}>
        <h3>Personalized Solutions</h3>
        {practicedToday ? (
          <div
            style={{
              fontSize: "1.1rem",
              color: "#317d52",
              padding: 15,
              borderRadius: 12,
              backgroundColor: "#daf4ec",
              maxWidth: 320,
              margin: "12px auto 0",
              textAlign: "center",
              userSelect: "text",
            }}
            aria-live="polite"
          >
            Thank you for practicing your personalized solutions today!
            <br />
            Please come back tomorrow to practice again.
          </div>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, maxWidth: 320 }}>
            <li style={{ marginBottom: 12 }}>
              <button
                style={navButtonStyle}
                onClick={() => navigate("/quick-destress")}
                aria-label="Navigate to Quick Destress tool"
              >
                Quick Destress
              </button>
            </li>
            <li style={{ marginBottom: 12 }}>
              <button
                style={navButtonStyle}
                onClick={() => navigate("/progressive-relaxation")}
                aria-label="Navigate to Progressive Relaxation tool"
              >
                Progressive Relaxation
              </button>
            </li>
            <li style={{ marginBottom: 12 }}>
              <button
                style={navButtonStyle}
                onClick={() => navigate("/vitamin-d-tracker")}
                aria-label="Navigate to Vitamin D Tracker"
              >
                Vitamin D Tracker
              </button>
            </li>
          </ul>
        )}
      </section>

      {/* NEW: Weekly Reflection card */}
      {aiPersonalizationConsent && hasEnoughLogsForWeekly && (
        <section style={{ marginBottom: 20 }}>
          <h3>Weekly reflection</h3>
          <div
            style={{
              backgroundColor: "#fdf5e6",
              borderRadius: 12,
              padding: 16,
              border: "1px solid #f5d27b",
              maxWidth: 420,
            }}
          >
            <p style={{ fontSize: 14, marginBottom: 10 }}>
              See simple patterns from your recent Bolo logs and a tiny experiment you might try
              next week. This is for self-reflection only, not therapy or medical advice.
            </p>
            {weeklyError && (
              <p style={{ color: "red", fontSize: 13, marginBottom: 8 }}>{weeklyError}</p>
            )}
            <button
              onClick={handleGenerateWeeklyReflection}
              disabled={weeklyLoading}
              style={{
                backgroundColor: weeklyLoading ? "#aaa" : "#0077cc",
                color: "white",
                border: "none",
                borderRadius: 20,
                padding: "8px 20px",
                fontWeight: "600",
                cursor: weeklyLoading ? "wait" : "pointer",
              }}
            >
              {weeklyLoading ? "Generating..." : "Generate this week’s reflection"}
            </button>
          </div>
        </section>
      )}

      {/* Voice Emotion Manager */}
      <section style={{ marginBottom: 20 }}>
        <h3>Voice Emotion Management</h3>
        {voiceEmotionConsent ? (
          <VoiceEmotionEmbed onResult={(res) => console.log("Emotion:", res)} />
        ) : (
          <p style={{ fontSize: 14, color: "#555" }}>
            Voice emotion analysis is turned off. You can enable it in settings if you wish Bolo to
            use voice emotion for reflections.
          </p>
        )}
      </section>

      {/* Daily Mood & Practice */}
      <section style={{ marginBottom: 20 }}>
        <h3>Daily Mood & Practice</h3>
        <div>
          <label>
            Mood (1-5):
            <input
              type="number"
              min={1}
              max={5}
              value={mood}
              onChange={(e) =>
                setMood(Math.min(5, Math.max(1, +e.target.value)))
              }
              aria-label="Mood input (1-5)"
            />
          </label>
          <label style={{ marginLeft: 10 }}>
            <input
              type="checkbox"
              checked={didPractice}
              onChange={(e) => setDidPractice(e.target.checked)}
              aria-label="Indicate practiced today"
              disabled={practicedToday}
            />
            Practiced Today
          </label>
          <br />
          <textarea
            value={note}
            onChange={onNoteChange}
            placeholder="Notes"
            rows={2}
            style={{
              width: "100%",
              borderRadius: 5,
              border: formError ? "2px solid red" : "1px solid #ccc",
              marginTop: 8,
            }}
          />
          {formError && (
            <div style={{ color: "red", marginTop: 4 }} role="alert">
              {formError}
            </div>
          )}
          <button
            onClick={submitMoodEntry}
            disabled={practicedToday}
            style={{
              marginTop: 10,
              backgroundColor: practicedToday ? "#aaa" : "#FFD700",
              borderRadius: 20,
              padding: "10px 28px",
              border: "none",
              cursor: practicedToday ? "not-allowed" : "pointer",
              fontWeight: "bold",
            }}
            aria-label="Submit daily mood and practice"
          >
            Submit
          </button>
        </div>

        {moodData.length > 0 && (
          <div style={{ marginTop: 20, maxWidth: 400, height: 150 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={moodData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis domain={[1, 5]} allowDecimals={false} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="mood"
                  stroke="#FFD700"
                  strokeWidth={3}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>
    </main>
  );
}

function ProgressItem({ label, value }) {
  return (
    <div
      title={label}
      style={{
        textAlign: "center",
        minWidth: 100,
        padding: "10px 20px",
        backgroundColor: "#f0f6fa",
        borderRadius: 12,
        color: "#104f57",
        boxShadow: "0 2px 6px rgba(16,79,87,0.15)",
        userSelect: "none",
        fontWeight: "600",
        fontSize: 24,
      }}
    >
      {value}
      <div>{label}</div>
    </div>
  );
}

function SkillItem({ label, value = 0 }) {
  return (
    <div
      style={{
        backgroundColor: "#daf4ec",
        padding: 15,
        borderRadius: 12,
        minWidth: 130,
        color: "#105f37",
        boxShadow: "0 1px 4px rgba(16,95,55,0.15)",
        userSelect: "none",
        fontWeight: "700",
        fontSize: 20,
        cursor: "default",
        textAlign: "center",
      }}
    >
      {value}
      <div>{label}</div>
    </div>
  );
}

const navButtonStyle = {
  backgroundColor: "#0077cc",
  color: "white",
  border: "none",
  borderRadius: 20,
  padding: "10px 20px",
  fontWeight: "600",
  cursor: "pointer",
  userSelect: "none",
  width: "100%",
  textAlign: "left",
};

const listStyle = { listStyle: "none", padding: 0, maxWidth: 320, margin: 0 };
const listItemStyle = { marginBottom: 12 };
